import { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Filter, ChevronDown, ArrowLeft, ArrowRight, ShoppingCart, Eye, Heart } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { productsApi, SearchRequest, SearchResponse, FiltersResponse } from "@/services/productsApi";
import { useCart } from "@/contexts/CartContext";
import { cn } from "@/lib/utils";

export default function ProductList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();
  
  // State
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [products, setProducts] = useState<SearchResponse | null>(null);
  const [filters, setFilters] = useState<FiltersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState(() => {
    const categoryParam = searchParams.get("categorie");
    return categoryParam || "all";
  });
  const [priceMin, setPriceMin] = useState(searchParams.get("price_min") || "");
  const [priceMax, setPriceMax] = useState(searchParams.get("price_max") || "");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 500000]);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string[]>>({});
  
  const currentPage = parseInt(searchParams.get("page") || "1");
  const pageSize = 24;

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      updateUrlAndSearch();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedCategory, priceMin, priceMax, selectedAttributes]);

  // Sync price inputs with slider
  useEffect(() => {
    setPriceMin(priceRange[0].toString());
    setPriceMax(priceRange[1].toString());
  }, [priceRange]);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Load filters when category changes
  useEffect(() => {
    loadFilters();
  }, [selectedCategory]);

  // Load products when URL changes
  useEffect(() => {
    searchProducts();
  }, [searchParams]);

  const loadCategories = async () => {
    try {
      const response = await fetch('https://moncopieur.cloud-erp.ma/ws/product_category');
      const categoriesData = await response.json();
      setCategories(categoriesData);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadFilters = async () => {
    setFiltersLoading(true);
    try {
      const data = await productsApi.getFilters();
      setFilters(data);
    } catch (error) {
      console.error("Failed to load filters:", error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const searchProducts = async () => {
    try {
      setLoading(true);
      const searchRequest: SearchRequest = {
        page_number: currentPage,
        page_size: pageSize,
      };

      if (searchTerm) searchRequest.name = searchTerm;
      if (selectedCategory && selectedCategory !== "all") searchRequest.categorie = selectedCategory;
      // Note: price filters removed from API but keeping UI for user experience
      if (Object.keys(selectedAttributes).length > 0) {
        searchRequest.attributes = selectedAttributes;
      }

      const data = await productsApi.searchProducts(searchRequest);
      setProducts(data);
    } catch (error) {
      console.error("Failed to search products:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateUrlAndSearch = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (selectedCategory && selectedCategory !== "all") params.set("categorie", selectedCategory);
    if (priceMin) params.set("price_min", priceMin);
    if (priceMax) params.set("price_max", priceMax);
    if (Object.keys(selectedAttributes).length > 0) {
      params.set("attributes", JSON.stringify(selectedAttributes));
    }
    params.set("page", "1"); // Reset to first page on filter change
    setSearchParams(params);
    
    // Store last search in sessionStorage for ProductDetail navigation
    sessionStorage.setItem("lastProductSearch", params.toString());
  };

  const handleAttributeChange = (attributeName: string, value: string, checked: boolean) => {
    setSelectedAttributes(prev => {
      const newAttributes = { ...prev };
      if (checked) {
        if (!newAttributes[attributeName]) {
          newAttributes[attributeName] = [];
        }
        newAttributes[attributeName].push(value);
      } else {
        if (newAttributes[attributeName]) {
          newAttributes[attributeName] = newAttributes[attributeName].filter(v => v !== value);
          if (newAttributes[attributeName].length === 0) {
            delete newAttributes[attributeName];
          }
        }
      }
      return newAttributes;
    });
  };

  const clearFilters = () => {
    setSearchTerm("");
    if (categories.length > 0) {
      setSelectedCategory("all");
    }
    setSelectedAttributes({});
    setSearchParams(new URLSearchParams());
  };

  const changePage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", page.toString());
    setSearchParams(params);
  };

  const totalPages = products ? Math.ceil(products.total / pageSize) : 0;

  const paginationRange = useMemo(() => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  }, [currentPage, totalPages]);

  return (
    <div className="container mx-auto p-4 lg:p-6">
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        {/* Sidebar Filters */}
        <div className="w-full lg:w-1/4 order-1 lg:order-1">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">Filtres</h2>
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  Effacer
                </Button>
              </div>
              
              {/* Category Filter */}
              <div className="mb-6">
                <label className="text-sm font-medium mb-2 block">Catégorie</label>
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes les catégories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les catégories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.name}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price Range Filter */}
              <div className="mb-6">
                <label className="text-sm font-medium mb-3 block">Gamme de prix (MAD)</label>
                <div className="space-y-3">
                  <Slider
                    value={priceRange}
                    onValueChange={(value) => setPriceRange(value as [number, number])}
                    max={500000}
                    min={0}
                    step={1000}
                    className="w-full"
                  />
                  <div className="flex items-center gap-2 text-sm">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={priceMin}
                      onChange={(e) => setPriceMin(e.target.value)}
                      className="w-20 h-8"
                    />
                    <span>-</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={priceMax}
                      onChange={(e) => setPriceMax(e.target.value)}
                      className="w-20 h-8"
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{priceRange[0].toLocaleString()} MAD</span>
                    <span>{priceRange[1].toLocaleString()} MAD</span>
                  </div>
                </div>
              </div>

              {/* Attribute Filters */}
              {filters && !filtersLoading && (
                <div className="space-y-4">
                  {filters.attributes.map((attribute) => (
                    <Collapsible key={attribute.attribute_name} defaultOpen>
                      <CollapsibleTrigger className="flex items-center justify-between w-full text-left">
                        <span className="text-sm font-medium">{attribute.attribute_name}</span>
                        <ChevronDown className="h-4 w-4" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2 space-y-2">
                        {attribute.values.map((value) => (
                          <div key={value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`${attribute.attribute_name}-${value}`}
                              checked={selectedAttributes[attribute.attribute_name]?.includes(value) || false}
                              onCheckedChange={(checked) => 
                                handleAttributeChange(attribute.attribute_name, value, checked as boolean)
                              }
                            />
                            <label
                              htmlFor={`${attribute.attribute_name}-${value}`}
                              className="text-sm cursor-pointer"
                            >
                              {value}
                            </label>
                          </div>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              )}

              {filtersLoading && (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i}>
                      <Skeleton className="h-4 w-20 mb-2" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="w-full lg:w-3/4 order-2 lg:order-2">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Rechercher des produits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Results Summary */}
          {products && (
            <div className="mb-4 text-sm text-muted-foreground">
              {products.total} produit{products.total !== 1 ? 's' : ''} trouvé{products.total !== 1 ? 's' : ''}
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="w-full h-48 mb-4" />
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2 mb-2" />
                    <Skeleton className="h-6 w-1/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : products && products.items.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.items.map((product) => (
                <Card key={product.id} className="group hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    {/* Product Image */}
                    <div className="relative mb-4 bg-muted rounded-lg overflow-hidden aspect-square">
                      {product.image_base64 ? (
                        <img
                          src={`data:image/jpeg;base64,${product.image_base64}`}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <span className="text-sm">Pas d'image</span>
                        </div>
                      )}
                      
                      {/* Hover Actions */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => addToCart({
                            id: product.id.toString(),
                            name: product.name,
                            price: 0, // No price in new API
                            image: product.image_base64 ? `data:image/jpeg;base64,${product.image_base64}` : undefined,
                            sku: product.id.toString(),
                          })}
                        >
                          <ShoppingCart className="h-4 w-4 mr-1" />
                          Ajouter
                        </Button>
                        {product.slug && (
                          <Button size="sm" variant="secondary" asChild>
                            <Link to={`/produit/${product.slug}`}>
                              <Eye className="h-4 w-4 mr-1" />
                              Voir
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="space-y-2">
                      <h3 className="font-semibold text-sm line-clamp-2">{product.name}</h3>
                      {product.brand && (
                        <p className="text-xs text-muted-foreground">{product.brand}</p>
                      )}
                      <p className="text-xs text-muted-foreground">ID: {product.id}</p>
                      {product.short_description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {product.short_description}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-semibold mb-2">Aucun produit trouvé</h3>
              <p className="text-muted-foreground mb-4">
                Essayez de modifier vos critères de recherche
              </p>
              <Button onClick={clearFilters} variant="outline">
                Effacer les filtres
              </Button>
            </div>
          )}

          {/* Pagination */}
          {products && products.total > pageSize && (
            <div className="mt-8 flex justify-center">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => changePage(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Précédent
                </Button>
                
                {paginationRange.map((page, index) => (
                  <Button
                    key={index}
                    variant={page === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => typeof page === 'number' && changePage(page)}
                    disabled={page === '...'}
                    className={cn(
                      page === '...' && "cursor-default"
                    )}
                  >
                    {page}
                  </Button>
                ))}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => changePage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Suivant
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}